import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import styles from './Login.module.css';

function clearAuthStorage() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('admin');
}

export default function Login() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [form, setForm] = useState({ email: '', password: '' });
  const [otp, setOtp] = useState('');
  const [otpHint, setOtpHint] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Only send to dashboard if an existing token is still valid
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setReady(true);
      return;
    }

    api
      .get('/auth/me')
      .then(() => {
        if (!cancelled) navigate('/dashboard', { replace: true });
      })
      .catch(() => {
        clearAuthStorage();
        if (!cancelled) setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const startResendTimer = () => {
    setResendCooldown(30);
    const t = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) {
          clearInterval(t);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const handleCredentials = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', form);
      if (data.requiresOtp) {
        // Drop any stale session so OTP step is not interrupted
        clearAuthStorage();
        setOtpHint(data.message || 'OTP sent');
        setStep('otp');
        setOtp('');
        startResendTimer();
      } else if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('admin', JSON.stringify(data.admin));
        navigate('/dashboard');
      } else {
        setError('Unexpected login response. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/verify-otp', {
        email: form.email,
        otp: otp.trim(),
      });
      if (!data.accessToken || !data.refreshToken) {
        setError('Login succeeded but tokens were missing. Try again.');
        return;
      }
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('admin', JSON.stringify(data.admin));
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/resend-otp', { email: form.email });
      setOtpHint('OTP resent');
      startResendTimer();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not resend OTP');
    } finally {
      setLoading(false);
    }
  };

  if (!ready) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <p className={styles.sub}>Checking session...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.icon}>⚙️</div>
        <h1 className={styles.title}>Admin Panel</h1>
        <p className={styles.sub}>
          {step === 'credentials'
            ? 'Sign in to continue'
            : 'Enter the OTP sent to your admin email'}
        </p>

        {error && <div className={styles.error}>⚠️ {error}</div>}
        {step === 'otp' && otpHint && !error && (
          <div className={styles.info}>✉️ {otpHint}</div>
        )}

        {step === 'credentials' ? (
          <form onSubmit={handleCredentials} className={styles.form}>
            <div className={styles.field}>
              <label>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="admin@app.com"
                required
                autoComplete="username"
              />
            </div>
            <div className={styles.field}>
              <label>Password</label>
              <div className={styles.passWrap}>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button type="button" onClick={() => setShowPass(!showPass)}>
                  {showPass ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            <button type="submit" className={styles.submit} disabled={loading}>
              {loading ? 'Checking...' : 'Continue'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className={styles.form}>
            <div className={styles.field}>
              <label>6-digit OTP</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                placeholder="••••••"
                required
                autoFocus
                style={{
                  letterSpacing: 8,
                  fontSize: 22,
                  fontWeight: 700,
                  textAlign: 'center',
                }}
              />
            </div>
            <button
              type="submit"
              className={styles.submit}
              disabled={loading || otp.length !== 6}
            >
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </button>
            <div className={styles.otpActions}>
              <button
                type="button"
                className={styles.linkBtn}
                disabled={loading || resendCooldown > 0}
                onClick={handleResend}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
              </button>
              <button
                type="button"
                className={styles.linkBtn}
                disabled={loading}
                onClick={() => {
                  setStep('credentials');
                  setOtp('');
                  setError('');
                  setOtpHint('');
                }}
              >
                ← Back
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
